import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import * as d3 from "d3";
import { ArrowDownUp } from "lucide-react";

import {
  clampLevel,
  formatLevel,
  formatMeddContactSubtitleDisplay,
  getInitials,
  getRlBadgeClasses,
  isAccountTeamVendor,
  meddpiccCanonicalToMatrixKey,
  MEDD_MATRIX_ROLE_DEFS,
  pairMatchesContact,
  pairMatchesStakeholder,
  resolveCustomerMeddRoleLabel,
  resolveCustomerMemberTitle,
  resolveVendorRoleLabel,
  splitStakeholdersForMatrix,
} from "./relationshipUtils";
import {
  ACCOUNT_TEAM_AVATAR_BG,
  EXEC_SPONSOR_AVATAR_BG,
  getCustomerContactAvatarBg,
  getVendorProfileAvatarBg,
} from "../../../Utils/Helper/profileAvatarUi";

const FLOW_FILTERS = [
  { id: "all", label: "All" },
  { id: "strong", label: "Strong" },
  { id: "moderate", label: "Moderate" },
  { id: "weak", label: "Weak" },
  { id: "unassessed", label: "Unassessed" },
];

const MEDD_ORDER = {
  EB: 0,
  CH: 1,
  DM: 2,
  EX: 3,
  IN: 4,
  TB: 5,
  PR: 6,
  IT: 7,
  EU: 8,
};

/** Mapped-but-unassessed ribbons — darker/thicker than prototype default for legibility. */
const UNASSESSED_LINK_STROKE = "#64748b";
const UNASSESSED_LINK_OPACITY = 0.58;
const UNASSESSED_LINK_WIDTH = 1.5;
const UNASSESSED_LINK_DASH = "5 4";

const NEXT_ACTIONS = {
  null: "Schedule an intro meeting to start the relationship.",
  0: "Schedule an intro meeting — no relationship established yet.",
  1: "Send a relevant insight or case study; request a brief call.",
  2: "Deepen with a structured discovery; involve a sponsor.",
  3: "Align on roadmap; propose a joint initiative or proof of value.",
  4: "Leverage as a champion; co-create exec briefing content.",
  5: "Mobilise as a strategic advocate; invite to reference activities.",
};

/** Prototype-style ribbon draw-in (~2.5s total across all visible arcs). */
const NETWORK_LINE_DRAW_MS = 650;
const NETWORK_LINE_DRAW_STAGGER_CAP_MS = 72;
const NETWORK_LINE_DRAW_BUDGET_MS = 2400;

function rlNetworkColor(rl) {
  if (rl === null || rl === undefined) return "#cbd5e1";
  if (rl >= 4) return "#16a34a";
  if (rl >= 2) return "#eab308";
  return "#dc2626";
}

function ribbonMatchesFilter(rl, filter) {
  if (filter === "all") return true;
  if (filter === "unassessed") return rl === null || rl === undefined;
  if (filter === "strong") return rl !== null && rl !== undefined && rl >= 4;
  if (filter === "moderate")
    return rl !== null && rl !== undefined && rl >= 2 && rl < 4;
  if (filter === "weak") return rl !== null && rl !== undefined && rl < 2;
  return true;
}

function resolveContactSubtitle(contact, considerMeddpiccRole = true) {
  if (considerMeddpiccRole !== false) {
    return formatMeddContactSubtitleDisplay(
      resolveCustomerMeddRoleLabel(contact) ||
        MEDD_MATRIX_ROLE_DEFS[contact.meddKey]?.label ||
        contact.meddLabel ||
        ""
    );
  }
  return (resolveCustomerMemberTitle(contact) || contact.title || "").trim();
}

function enrichContact(contact, considerMeddpiccRole = true) {
  const meddKey = meddpiccCanonicalToMatrixKey(
    contact.meddpiccRoleCanonical ||
      contact.raw?.meddpicc_role ||
      contact.meddpiccRole
  );
  const meddLabel =
    resolveCustomerMeddRoleLabel(contact) ||
    MEDD_MATRIX_ROLE_DEFS[meddKey]?.label ||
    contact.meddpiccRole ||
    "";
  const enriched = { ...contact, meddKey, meddLabel };
  return {
    ...enriched,
    subtitleLabel: resolveContactSubtitle(enriched, considerMeddpiccRole),
  };
}

function truncateLabel(text, max = 28) {
  const s = String(text || "").trim();
  if (!s) return "";
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function buildGraphModel(
  stakeholders,
  contacts,
  pairs,
  considerMeddpiccRole = true
) {
  const { execTeam, accountTeam } = splitStakeholdersForMatrix(stakeholders);
  const vendors = [...execTeam, ...accountTeam].map(v => {
    const raw = v.raw || {};
    const roleLabel = isAccountTeamVendor(v)
      ? String(
          v.member_title || raw.member_title || v.title || raw.title || ""
        ).trim()
      : resolveVendorRoleLabel(v);
    return { ...v, roleLabel };
  });
  const sortedContacts = [...(contacts || [])]
    .map(contact => enrichContact(contact, considerMeddpiccRole))
    .sort((a, b) => {
      if (considerMeddpiccRole !== false) {
        return (MEDD_ORDER[a.meddKey] ?? 9) - (MEDD_ORDER[b.meddKey] ?? 9);
      }
      return String(a.name || "").localeCompare(
        String(b.name || ""),
        undefined,
        {
          sensitivity: "base",
        }
      );
    });

  const edges = [];
  for (const pair of pairs || []) {
    const vendor = vendors.find(v => pairMatchesStakeholder(pair, v.id));
    const contact = sortedContacts.find(c => pairMatchesContact(pair, c));
    if (!vendor || !contact) continue;
    const rl =
      pair.rlLevel !== undefined && pair.rlLevel !== null
        ? clampLevel(pair.rlLevel)
        : null;
    edges.push({
      pair,
      vendor,
      contact,
      rl,
      assessed: rl !== null,
      key: `${vendor.id}-${contact.id}`,
    });
  }

  const vendorById = new Map(vendors.map(v => [v.id, v]));
  const linkedEdges = edges.map(e => ({
    ...e,
    vendor: vendorById.get(e.vendor.id) || e.vendor,
  }));

  return { vendors, contacts: sortedContacts, edges: linkedEdges };
}

function drawFlowGraph({
  svgEl,
  width,
  height,
  model,
  flowFilter,
  onSelectEdge,
  onSelectVendor,
  onSelectContact,
  onHoverEdge,
  animateDraw = true,
  focusVendorId = null,
  focusContactId = null,
}) {
  const { vendors, contacts, edges } = model;
  const svg = d3.select(svgEl);
  svg.selectAll("*").remove();
  svg.attr("viewBox", `0 0 ${width} ${height}`);

  const PAD = { top: 52, bot: 28 };
  const lx = 148;
  const rx = width - 148;
  const nodeR = 14;
  const nV = Math.max(1, vendors.length);
  const nC = Math.max(1, contacts.length);
  const usableH = height - PAD.top - PAD.bot;
  const rawVSlot = usableH / nV;
  const rawCSlot = usableH / nC;
  const vSlot = 2 * nodeR + (rawVSlot - 2 * nodeR) * 0.5;
  const cSlot = 2 * nodeR + (rawCSlot - 2 * nodeR) * 0.5;
  const vY = i => PAD.top + i * vSlot + vSlot / 2;
  const cY = i => PAD.top + i * cSlot + cSlot / 2;
  const mx = (lx + rx) / 2;

  const vendorIndex = new Map(vendors.map((v, i) => [v.id, i]));
  const contactIndex = new Map(contacts.map((c, i) => [c.id, i]));

  svg
    .append("text")
    .attr("x", lx)
    .attr("y", PAD.top - 22)
    .attr("text-anchor", "middle")
    .attr("font-size", 10)
    .attr("font-weight", 700)
    .attr("fill", EXEC_SPONSOR_AVATAR_BG)
    .attr("letter-spacing", 0.8)
    .text("EXEC + ACCOUNT TEAM");
  svg
    .append("text")
    .attr("x", rx)
    .attr("y", PAD.top - 22)
    .attr("text-anchor", "middle")
    .attr("font-size", 10)
    .attr("font-weight", 700)
    .attr("fill", getCustomerContactAvatarBg())
    .attr("letter-spacing", 0.8)
    .text("ACCOUNT CONTACTS");

  const ribbonG = svg.append("g").attr("class", "sk-links");
  const labelG = svg.append("g").attr("class", "sk-labels");

  const visibleEdges = edges.filter(e => ribbonMatchesFilter(e.rl, flowFilter));
  const staggerMs =
    visibleEdges.length > 0
      ? Math.min(
          NETWORK_LINE_DRAW_STAGGER_CAP_MS,
          NETWORK_LINE_DRAW_BUDGET_MS / visibleEdges.length
        )
      : 0;

  visibleEdges.forEach((edge, ribbonIdx) => {
    const vi = vendorIndex.get(edge.vendor.id);
    const ci = contactIndex.get(edge.contact.id);
    if (vi === undefined || ci === undefined) return;

    const col = rlNetworkColor(edge.rl);
    const lw = edge.assessed
      ? Math.max(1.5, 1.5 + (edge.rl || 0) * 0.7)
      : UNASSESSED_LINK_WIDTH;
    const vy2 = vY(vi);
    const cy2 = cY(ci);
    const pathD = `M${lx},${vy2} C${mx},${vy2} ${mx},${cy2} ${rx},${cy2}`;

    const path = ribbonG
      .append("path")
      .attr("d", pathD)
      .attr("stroke", edge.assessed ? col : UNASSESSED_LINK_STROKE)
      .attr("stroke-width", lw)
      .attr("fill", "none")
      .attr("opacity", edge.assessed ? 0.72 : UNASSESSED_LINK_OPACITY)
      .attr("class", "sk-link")
      .attr("data-vid", edge.vendor.id)
      .attr("data-source-id", edge.vendor.sourceId || "")
      .attr("data-cid", edge.contact.id)
      .attr("data-assessed", edge.assessed ? "1" : "0")
      .attr("data-lw", lw)
      .style("cursor", "pointer");

    if (!edge.assessed) {
      path.attr("stroke-dasharray", UNASSESSED_LINK_DASH);
      if (animateDraw) {
        path
          .attr("opacity", 0)
          .transition()
          .delay(ribbonIdx * staggerMs)
          .duration(Math.min(400, NETWORK_LINE_DRAW_MS))
          .ease(d3.easeCubicOut)
          .attr("opacity", UNASSESSED_LINK_OPACITY);
      }
    } else {
      const pathNode = path.node();
      const totalLen = pathNode.getTotalLength();

      if (animateDraw) {
        path
          .attr("stroke-dasharray", `${totalLen}`)
          .attr("stroke-dashoffset", totalLen)
          .attr("opacity", 0.72)
          .transition()
          .delay(ribbonIdx * staggerMs)
          .duration(NETWORK_LINE_DRAW_MS)
          .ease(d3.easeCubicOut)
          .attr("stroke-dashoffset", 0)
          .on("end", function onDrawEnd() {
            d3.select(this)
              .attr("stroke-dasharray", null)
              .attr("stroke-dashoffset", null);
          });

        const midPt = pathNode.getPointAtLength(totalLen * 0.5);
        labelG
          .append("text")
          .attr("x", midPt.x)
          .attr("y", midPt.y - 4)
          .attr("text-anchor", "middle")
          .attr("font-size", 9)
          .attr("font-weight", 700)
          .attr("fill", col)
          .attr("opacity", 0)
          .attr("pointer-events", "none")
          .text(`RL ${edge.rl}`)
          .transition()
          .delay(ribbonIdx * staggerMs + NETWORK_LINE_DRAW_MS * 0.65)
          .duration(280)
          .ease(d3.easeCubicOut)
          .attr("opacity", 0.8);
      } else {
        const midPt = pathNode.getPointAtLength(totalLen * 0.5);
        labelG
          .append("text")
          .attr("x", midPt.x)
          .attr("y", midPt.y - 4)
          .attr("text-anchor", "middle")
          .attr("font-size", 9)
          .attr("font-weight", 700)
          .attr("fill", col)
          .attr("opacity", 0.8)
          .attr("pointer-events", "none")
          .text(`RL ${edge.rl}`);
      }
    }

    path
      .on("mouseover", function (evt) {
        d3.selectAll(".sk-link").attr("opacity", 0.06);
        d3.select(this)
          .attr("opacity", 1)
          .attr("stroke-width", lw + 2);
        onHoverEdge?.(edge, evt);
      })
      .on("mousemove", evt => onHoverEdge?.(edge, evt))
      .on("mouseout", () => {
        d3.selectAll(".sk-link").each(function () {
          const sel = d3.select(this);
          const assessed = sel.attr("data-assessed") === "1";
          const focused =
            (focusVendorId &&
              (sel.attr("data-vid") === String(focusVendorId) ||
                sel.attr("data-source-id") === String(focusVendorId))) ||
            (!focusVendorId &&
              focusContactId &&
              sel.attr("data-cid") === String(focusContactId));
          if (focusVendorId || focusContactId) {
            sel
              .attr("opacity", focused ? 1 : 0.06)
              .attr(
                "stroke-width",
                sel.attr("data-lw") || UNASSESSED_LINK_WIDTH
              );
            return;
          }
          sel
            .attr("opacity", assessed ? 0.72 : UNASSESSED_LINK_OPACITY)
            .attr("stroke-width", sel.attr("data-lw") || UNASSESSED_LINK_WIDTH);
        });
        onHoverEdge?.(null);
      })
      .on("click", () => onSelectEdge?.(edge));
  });

  if (focusVendorId || focusContactId) {
    d3.select(svgEl)
      .selectAll(".sk-link")
      .each(function () {
        const sel = d3.select(this);
        const focused =
          (focusVendorId &&
            (sel.attr("data-vid") === String(focusVendorId) ||
              sel.attr("data-source-id") === String(focusVendorId))) ||
          (!focusVendorId &&
            focusContactId &&
            sel.attr("data-cid") === String(focusContactId));
        sel.attr("opacity", focused ? 1 : 0.06);
      });
  }

  const nodeG = svg.append("g").attr("class", "sk-nodes");

  const nodeFadeDelay =
    animateDraw && visibleEdges.length > 0
      ? Math.min(
          NETWORK_LINE_DRAW_BUDGET_MS,
          (visibleEdges.length - 1) * staggerMs + NETWORK_LINE_DRAW_MS * 0.35
        )
      : 0;
  const nodeFadeDuration = animateDraw ? 420 : 0;

  vendors.forEach((v, vi) => {
    const isTeam = isAccountTeamVendor(v);
    const fill = getVendorProfileAvatarBg(isTeam);
    const vy2 = vY(vi);
    const g = nodeG
      .append("g")
      .style("cursor", "pointer")
      .attr("class", "sk-vnode")
      .attr("data-id", v.id)
      .attr("opacity", animateDraw ? 0 : 1);
    if (animateDraw) {
      g.transition()
        .delay(nodeFadeDelay + vi * 24)
        .duration(nodeFadeDuration)
        .ease(d3.easeCubicOut)
        .attr("opacity", 1);
    }
    g.append("circle")
      .attr("cx", lx)
      .attr("cy", vy2)
      .attr("r", nodeR)
      .attr("fill", fill)
      .attr("stroke", "#fff")
      .attr("stroke-width", 2);
    g.append("text")
      .attr("x", lx)
      .attr("y", vy2)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("font-size", 8.5)
      .attr("font-weight", 700)
      .attr("fill", "#fff")
      .text(getInitials(v.name));
    const name = v.name.length > 17 ? `${v.name.substring(0, 16)}…` : v.name;
    g.append("text")
      .attr("x", lx - nodeR - 8)
      .attr("y", vy2 - 6)
      .attr("text-anchor", "end")
      .attr("dominant-baseline", "middle")
      .attr("font-size", 11)
      .attr("font-weight", 600)
      .attr("fill", "#1e293b")
      .text(name);
    if (v.roleLabel) {
      g.append("text")
        .attr("x", lx - nodeR - 8)
        .attr("y", vy2 + 9)
        .attr("text-anchor", "end")
        .attr("dominant-baseline", "middle")
        .attr("font-size", 9.5)
        .attr("fill", "#94a3b8")
        .text(truncateLabel(v.roleLabel));
    }
    g.on("mouseover", () => {
      d3.selectAll(".sk-link").attr("opacity", function () {
        return d3.select(this).attr("data-vid") === v.id ? 1 : 0.05;
      });
    })
      .on("mouseout", () => {
        d3.selectAll(".sk-link").attr("opacity", function () {
          return d3.select(this).attr("data-assessed") === "1"
            ? 0.72
            : UNASSESSED_LINK_OPACITY;
        });
      })
      .on("click", () => onSelectVendor?.(v));
  });

  const contactAvatarBg = getCustomerContactAvatarBg();
  contacts.forEach((c, ci) => {
    const cy2 = cY(ci);
    const g = nodeG
      .append("g")
      .style("cursor", "pointer")
      .attr("class", "sk-cnode")
      .attr("data-id", c.id)
      .attr("opacity", animateDraw ? 0 : 1);
    if (animateDraw) {
      g.transition()
        .delay(nodeFadeDelay + ci * 24)
        .duration(nodeFadeDuration)
        .ease(d3.easeCubicOut)
        .attr("opacity", 1);
    }
    g.append("circle")
      .attr("cx", rx)
      .attr("cy", cy2)
      .attr("r", nodeR)
      .attr("fill", contactAvatarBg)
      .attr("stroke", "#fff")
      .attr("stroke-width", 2);
    g.append("text")
      .attr("x", rx)
      .attr("y", cy2)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("font-size", 8.5)
      .attr("font-weight", 700)
      .attr("fill", "#fff")
      .text(getInitials(c.name));
    const name = c.name.length > 17 ? `${c.name.substring(0, 16)}…` : c.name;
    g.append("text")
      .attr("x", rx + nodeR + 8)
      .attr("y", cy2 - 6)
      .attr("text-anchor", "start")
      .attr("dominant-baseline", "middle")
      .attr("font-size", 11)
      .attr("font-weight", 600)
      .attr("fill", "#1e293b")
      .text(name);
    if (c.subtitleLabel) {
      g.append("text")
        .attr("x", rx + nodeR + 8)
        .attr("y", cy2 + 9)
        .attr("text-anchor", "start")
        .attr("dominant-baseline", "middle")
        .attr("font-size", 9.5)
        .attr("fill", "#94a3b8")
        .text(truncateLabel(c.subtitleLabel));
    }
    g.on("mouseover", () => {
      d3.selectAll(".sk-link").attr("opacity", function () {
        return d3.select(this).attr("data-cid") === c.id ? 1 : 0.05;
      });
    })
      .on("mouseout", () => {
        d3.selectAll(".sk-link").attr("opacity", function () {
          return d3.select(this).attr("data-assessed") === "1"
            ? 0.72
            : UNASSESSED_LINK_OPACITY;
        });
      })
      .on("click", () => onSelectContact?.(c));
  });
}

function NetworkSidebar({
  selection,
  pairs,
  gradeMapping,
  getGradeLabel,
  onAssess,
  onOpenCoverage,
}) {
  if (!selection) {
    return (
      <div className="ng-sb-empty">
        Click a node or relationship arc to see details here.
      </div>
    );
  }

  if (selection.type === "edge") {
    const { edge } = selection;
    const { vendor, contact, pair, rl } = edge;
    const rlTxt = rl !== null ? formatLevel("RL", rl) : "RL ?";
    const nextAction = NEXT_ACTIONS[rl !== null ? rl : null] || "";
    return (
      <>
        <div className="ng-node-detail">
          <div className="ng-nd-name">{vendor.name}</div>
          <div className="ng-nd-title">
            {vendor.roleLabel || vendor.title || ""}
          </div>
          <div
            style={{
              margin: "6px 0",
              display: "flex",
              color: "var(--text-muted)",
            }}
            aria-hidden
          >
            <ArrowDownUp size={12} strokeWidth={2} />
          </div>
          <div className="ng-nd-name">{contact.name}</div>
          <div className="ng-nd-title">{contact.subtitleLabel || ""}</div>
          <div className="mt-2">
            <span
              className={`rounded-full border px-2 py-0.5 text-xs font-bold ${rl !== null ? getRlBadgeClasses(rl) : "border-slate-300 bg-slate-50 text-slate-500"}`}
              title={rl !== null ? undefined : "Unassessed"}
            >
              {rlTxt}
            </span>
          </div>
          {pair?.rationale ? (
            <div
              style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}
            >
              {pair.rationale}
            </div>
          ) : null}
          <div
            style={{
              fontSize: 10.5,
              color: "var(--text-muted)",
              marginTop: 8,
              paddingTop: 8,
              borderTop: "1px solid var(--border)",
            }}
          >
            {nextAction}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-pri btn-sm btn-full mt-2"
          onClick={() => onAssess?.({ pair, stakeholder: vendor, contact })}
        >
          {rl !== null ? "Update" : "Assess"} Relationship
        </button>
      </>
    );
  }

  if (selection.type === "vendor") {
    const vendor = selection.vendor;
    const related = (pairs || [])
      .map(pair => {
        if (!pairMatchesStakeholder(pair, vendor.id)) return null;
        const contact = selection.contacts.find(c =>
          pairMatchesContact(pair, c)
        );
        if (!contact) return null;
        const rl =
          pair.rlLevel !== null && pair.rlLevel !== undefined
            ? clampLevel(pair.rlLevel)
            : null;
        return { pair, contact, rl };
      })
      .filter(Boolean);

    return (
      <>
        <div className="ng-node-detail">
          <div className="ng-nd-name">{vendor.name}</div>
          <div className="ng-nd-title">
            {vendor.roleLabel || vendor.title || ""}
          </div>
        </div>
        <div className="ng-edge-list">
          {related.map(({ pair, contact, rl }) => (
            <button
              key={`${vendor.id}-${contact.id}`}
              type="button"
              className="ng-edge-item"
              style={{ width: "100%", textAlign: "left" }}
              onClick={() => onAssess?.({ pair, stakeholder: vendor, contact })}
            >
              <div className="ng-ei-pair">
                {contact.name}{" "}
                {contact.subtitleLabel ? (
                  <span style={{ fontSize: 10, color: "var(--text-muted)" }}>
                    {contact.subtitleLabel}
                  </span>
                ) : null}
              </div>
              <div style={{ marginTop: 3 }}>
                <span
                  className={`rounded-full border px-2 py-0.5 text-xs font-bold ${rl !== null ? getRlBadgeClasses(rl) : "border-slate-300 bg-slate-50 text-slate-500"}`}
                  title={rl !== null ? undefined : "Unassessed"}
                >
                  {rl !== null ? formatLevel("RL", rl) : "RL ?"}
                </span>
              </div>
            </button>
          ))}
        </div>
      </>
    );
  }

  if (selection.type === "contact") {
    const contact = selection.contact;
    const related = (pairs || [])
      .map(pair => {
        if (!pairMatchesContact(pair, contact)) return null;
        const vendor = selection.vendors.find(v =>
          pairMatchesStakeholder(pair, v.id)
        );
        if (!vendor) return null;
        const rl =
          pair.rlLevel !== null && pair.rlLevel !== undefined
            ? clampLevel(pair.rlLevel)
            : null;
        return { pair, vendor, rl };
      })
      .filter(Boolean);

    return (
      <>
        <div className="ng-node-detail">
          <div className="ng-nd-name">{contact.name}</div>
          <div className="ng-nd-title">{contact.subtitleLabel || ""}</div>
        </div>
        <div className="ng-edge-list">
          {related.map(({ pair, vendor, rl }) => (
            <button
              key={`${vendor.id}-${contact.id}`}
              type="button"
              className="ng-edge-item"
              style={{ width: "100%", textAlign: "left" }}
              onClick={() => onAssess?.({ pair, stakeholder: vendor, contact })}
            >
              <div className="ng-ei-pair">{vendor.name}</div>
              <div style={{ marginTop: 3 }}>
                <span
                  className={`rounded-full border px-2 py-0.5 text-xs font-bold ${rl !== null ? getRlBadgeClasses(rl) : "border-slate-300 bg-slate-50 text-slate-500"}`}
                  title={rl !== null ? undefined : "Unassessed"}
                >
                  {rl !== null ? formatLevel("RL", rl) : "RL ?"}
                </span>
              </div>
            </button>
          ))}
        </div>
      </>
    );
  }

  return null;
}

function RelationshipsNetworkMap({
  accountName,
  stakeholders,
  contacts,
  pairs,
  gradeMapping,
  getGradeLabel,
  considerMeddpiccRole = true,
  mapDrawGeneration = 0,
  initialConsumedMapDrawGen = 0,
  onMapDrawGenerationConsumed,
  onAssess,
  onOpenCoverage,
  focusVendorId = null,
  focusContactId = null,
  focusLabel = "",
  onClearFocus,
}) {
  const [flowFilter, setFlowFilter] = useState("all");
  const [selection, setSelection] = useState(null);
  const [tip, setTip] = useState(null);
  const wrapRef = useRef(null);
  const svgRef = useRef(null);

  const model = useMemo(
    () => buildGraphModel(stakeholders, contacts, pairs, considerMeddpiccRole),
    [stakeholders, contacts, pairs, considerMeddpiccRole]
  );

  const hasMappings = model.edges.length > 0;
  const canRender = model.vendors.length > 0 && model.contacts.length > 0;

  const resetSelection = useCallback(() => setSelection(null), []);

  useEffect(() => {
    setSelection(null);
    setTip(null);
  }, [accountName, flowFilter, stakeholders, contacts, pairs]);

  const lastDrawSizeRef = useRef({ width: 0, height: 0 });
  const consumedMapDrawGenRef = useRef(initialConsumedMapDrawGen);
  const lastFlowFilterRef = useRef(flowFilter);
  const lastModelRef = useRef(model);

  useEffect(() => {
    const wrap = wrapRef.current;
    const svgEl = svgRef.current;
    if (!wrap || !svgEl || !canRender || !hasMappings) return undefined;

    const flowFilterChanged = lastFlowFilterRef.current !== flowFilter;
    const modelChanged = lastModelRef.current !== model;
    lastFlowFilterRef.current = flowFilter;
    lastModelRef.current = model;

    const shouldAnimateDraw = mapDrawGeneration > consumedMapDrawGenRef.current;

    let cancelled = false;
    let resizeObserver = null;
    let resizeDebounceTimer = null;
    let observeAttachTimer = null;
    let raf0 = 0;
    let raf1 = 0;

    const measureSize = () => ({
      width: wrap.clientWidth || 700,
      height: Math.max(400, wrap.clientHeight - 52) || 480,
    });

    const runDraw = (animateDraw = true) => {
      if (cancelled) return;
      const { width, height } = measureSize();
      const prev = lastDrawSizeRef.current;
      const sizeUnchanged =
        Math.abs(width - prev.width) < 2 && Math.abs(height - prev.height) < 2;
      if (
        !animateDraw &&
        sizeUnchanged &&
        !flowFilterChanged &&
        !modelChanged
      ) {
        return;
      }
      lastDrawSizeRef.current = { width, height };

      drawFlowGraph({
        svgEl,
        width,
        height,
        model,
        flowFilter,
        animateDraw,
        onSelectEdge: edge =>
          setSelection({
            type: "edge",
            edge,
            vendors: model.vendors,
            contacts: model.contacts,
          }),
        onSelectVendor: vendor =>
          setSelection({
            type: "vendor",
            vendor,
            vendors: model.vendors,
            contacts: model.contacts,
          }),
        onSelectContact: contact =>
          setSelection({
            type: "contact",
            contact,
            vendors: model.vendors,
            contacts: model.contacts,
          }),
        focusVendorId,
        focusContactId,
        onHoverEdge: (edge, evt) => {
          if (!edge || !evt) {
            setTip(null);
            return;
          }
          const rlTxt = edge.rl !== null ? formatLevel("RL", edge.rl) : "RL ?";
          setTip({
            left: evt.clientX + 14,
            top: evt.clientY - 24,
            html: (
              <>
                <strong>{edge.vendor.name}</strong>{" "}
                <span style={{ opacity: 0.6 }}>↔</span>{" "}
                <strong>{edge.contact.name}</strong>
                <br />
                <span style={{ opacity: 0.75, fontSize: 10.5 }}>
                  {edge.contact.subtitleLabel || ""}
                  {edge.contact.subtitleLabel && edge.vendor.roleLabel
                    ? " · "
                    : ""}
                  {edge.vendor.roleLabel || ""}
                </span>
                <br />
                <span
                  style={{
                    color: rlNetworkColor(edge.rl),
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                  title={edge.rl !== null ? undefined : "Unassessed"}
                >
                  {edge.rl !== null ? rlTxt : `${rlTxt} · Unassessed`}
                </span>
              </>
            ),
          });
        },
      });
    };

    const commitDraw = () => {
      if (cancelled) return;
      runDraw(shouldAnimateDraw);
      if (shouldAnimateDraw) {
        consumedMapDrawGenRef.current = mapDrawGeneration;
        onMapDrawGenerationConsumed?.(mapDrawGeneration);
      }
    };

    // Wait for layout (real width + path lengths). ResizeObserver on observe() cancels transitions.
    const scheduleDraw = (attempt = 0) => {
      if (cancelled) return;
      const layoutReady = wrap.clientWidth >= 80 || attempt >= 12;
      if (!layoutReady) {
        raf0 = requestAnimationFrame(() => scheduleDraw(attempt + 1));
        return;
      }
      raf0 = requestAnimationFrame(() => {
        raf1 = requestAnimationFrame(commitDraw);
      });
    };
    scheduleDraw();

    const resizeObserveDelayMs = shouldAnimateDraw
      ? NETWORK_LINE_DRAW_BUDGET_MS + 350
      : 0;

    if (typeof ResizeObserver !== "undefined") {
      const attachResizeObserver = () => {
        if (cancelled) return;
        resizeObserver = new ResizeObserver(() => {
          clearTimeout(resizeDebounceTimer);
          resizeDebounceTimer = setTimeout(() => {
            if (!cancelled) runDraw(false);
          }, 150);
        });
        resizeObserver.observe(wrap);
      };
      if (resizeObserveDelayMs > 0) {
        observeAttachTimer = setTimeout(
          attachResizeObserver,
          resizeObserveDelayMs
        );
      } else {
        attachResizeObserver();
      }
    }

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf0);
      cancelAnimationFrame(raf1);
      clearTimeout(resizeDebounceTimer);
      clearTimeout(observeAttachTimer);
      resizeObserver?.disconnect();
      d3.select(svgEl).selectAll("*").interrupt();
    };
  }, [
    model,
    flowFilter,
    canRender,
    hasMappings,
    mapDrawGeneration,
    focusVendorId,
    focusContactId,
    onMapDrawGenerationConsumed,
  ]);

  if (!canRender) {
    return (
      <div id="network-pane" className="active" style={{ marginTop: 8 }}>
        <div
          className="ng-canvas-wrap"
          style={{ justifyContent: "center", alignItems: "center" }}
        >
          <div
            style={{
              padding: 48,
              textAlign: "center",
              color: "var(--text-muted)",
              maxWidth: 420,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--gray-700)",
              }}
            >
              No contacts or vendors to map
            </div>
            <p style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
              Add customer contacts and assign exec or account team members
              before using the relationship map.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!hasMappings) {
    return (
      <div id="network-pane" className="active" style={{ marginTop: 8 }}>
        <div
          className="ng-canvas-wrap"
          style={{ justifyContent: "center", alignItems: "center" }}
        >
          <div
            style={{
              padding: 48,
              textAlign: "center",
              color: "var(--text-muted)",
              maxWidth: 420,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "var(--gray-700)",
              }}
            >
              No mapped relationships yet
            </div>
            <p style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
              Map vendor ↔ contact pairs in the Coverage Matrix first. Assessed
              relationships will appear as colored arcs.
            </p>
            {onOpenCoverage ? (
              <button
                type="button"
                className="btn btn-pri btn-sm mt-3"
                onClick={onOpenCoverage}
              >
                Open Coverage Matrix
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="network-pane" className="active" style={{ marginTop: 8 }}>
      {focusLabel ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
          <span>{focusLabel}</span>
          {onClearFocus ? (
            <button
              type="button"
              className="btn btn-sm btn-sec"
              onClick={onClearFocus}
            >
              Clear highlight
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="ng-canvas-wrap" ref={wrapRef}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            padding: "10px 12px 0",
          }}
        >
          <div className="ng-view-toggle">
            <button type="button" className="ng-vbtn active">
              Flow
            </button>
          </div>
          <div className="flow-filter-strip" id="flow-filter-strip">
            {FLOW_FILTERS.map(f => (
              <button
                key={f.id}
                type="button"
                className={`flow-flt${flowFilter === f.id ? " active" : ""}`}
                onClick={() => setFlowFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <svg
          id="ng-svg"
          ref={svgRef}
          role="img"
          aria-label={`${accountName} relationship flow map`}
        />
        <div
          id="ng-hint"
          style={{
            position: "absolute",
            bottom: 10,
            left: 12,
            fontSize: 11,
            color: "var(--text-muted)",
          }}
        >
          Hover a ribbon for details · Click to assess or pin in sidebar →
        </div>
        {tip ? (
          <div
            style={{
              position: "fixed",
              left: tip.left,
              top: tip.top,
              background: "#1e293b",
              color: "#fff",
              fontSize: 11,
              padding: "8px 12px",
              borderRadius: 8,
              pointerEvents: "none",
              zIndex: 9999,
              maxWidth: 260,
              lineHeight: 1.6,
              boxShadow: "0 4px 20px rgba(0,0,0,.35)",
            }}
          >
            {tip.html}
          </div>
        ) : null}
      </div>
      <div className="ng-sidebar">
        <div className="ng-sb-title">Details</div>
        <div id="ng-detail">
          <NetworkSidebar
            selection={selection}
            pairs={pairs}
            gradeMapping={gradeMapping}
            getGradeLabel={getGradeLabel}
            onAssess={payload => {
              resetSelection();
              onAssess?.(payload);
            }}
            onOpenCoverage={onOpenCoverage}
          />
        </div>
        <div id="ng-legend-wrap" style={{ marginTop: 12 }}>
          <div className="ng-sb-title">Legend</div>
          <div
            className="ng-legend"
            style={{
              border: "none",
              padding: 0,
              flexDirection: "column",
              gap: 6,
            }}
            id="ng-legend"
          >
            <div className="ng-leg-item">
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: EXEC_SPONSOR_AVATAR_BG,
                  flexShrink: 0,
                }}
              />
              Executive sponsor
            </div>
            <div className="ng-leg-item">
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: ACCOUNT_TEAM_AVATAR_BG,
                  flexShrink: 0,
                }}
              />
              Account team
            </div>
            <div className="ng-leg-item">
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: getCustomerContactAvatarBg(),
                  flexShrink: 0,
                }}
              />
              Account contact
            </div>
            <div className="ng-leg-item">
              <div className="ng-leg-line" style={{ background: "#16a34a" }} />{" "}
              RL 4–5 (Strong)
            </div>
            <div className="ng-leg-item">
              <div className="ng-leg-line" style={{ background: "#eab308" }} />{" "}
              RL 2–3 (Moderate)
            </div>
            <div className="ng-leg-item">
              <div className="ng-leg-line" style={{ background: "#dc2626" }} />{" "}
              RL 0–1 (Weak)
            </div>
            <div className="ng-leg-item" title="Unassessed">
              <div
                className="ng-leg-line"
                style={{
                  background: "#cbd5e1",
                  borderTop: `1.5px dashed ${UNASSESSED_LINK_STROKE}`,
                  height: 0,
                }}
              />
              Mapped · RL ?
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RelationshipsNetworkMap;
